import type { PhotoCategory } from "../shared/domain";
import type { UploadTicket } from "../shared/types";
import { ApiClientError, apiMutation, uploadFile } from "./api";
import { maxVehiclePhotoBytes, maxVehiclePhotosPerBatch, vehiclePhotoContentTypes } from "./vehicle-photo-intake";

const hashes = new WeakMap<File, Promise<string>>();
export function vehiclePhotoHash(file: File): Promise<string> {
  let pending = hashes.get(file);
  if (!pending) {
    pending = file.arrayBuffer().then(bytes => crypto.subtle.digest("SHA-256", bytes))
      .then(bytes => Array.from(new Uint8Array(bytes), b => b.toString(16).padStart(2, "0")).join(""));
    hashes.set(file, pending);
    void pending.catch(() => hashes.delete(file));
  }
  return pending;
}

/** SHA secuencial: no leer 40 fotos de 25 MB a la vez en móviles. */
export async function prepareVehiclePhotoFiles(current: readonly File[], incoming: readonly File[], knownHashes: readonly string[] = []) {
  const files = [...current];
  const seen = new Set(knownHashes);
  for (const file of current) seen.add(await vehiclePhotoHash(file));
  let duplicates = 0; let invalid = 0; let overflow = 0;
  for (const file of incoming) {
    if (!vehiclePhotoContentTypes.some(type => type === file.type) || file.size <= 0 || file.size > maxVehiclePhotoBytes) { invalid++; continue; }
    const hash = await vehiclePhotoHash(file);
    if (seen.has(hash)) { duplicates++; continue; }
    if (files.length >= maxVehiclePhotosPerBatch) { overflow++; continue; }
    seen.add(hash); files.push(file);
  }
  return { files, duplicates, invalid, overflow };
}

export async function uploadVehiclePhoto(vehicleId: string, file: File, category: PhotoCategory): Promise<{ resourceId: string; duplicate: boolean }> {
  const sha256 = await vehiclePhotoHash(file);
  const ticket = await apiMutation<UploadTicket | { duplicate: true; resourceId: string }>(`/vehicles/${vehicleId}/photos`, "POST", {
    category, fileName: file.name, contentType: file.type, sizeBytes: file.size, sha256,
  });
  if ("queued" in ticket) throw new Error("La subida de fotos necesita conexión.");
  if ("duplicate" in ticket) return ticket;
  try {
    await uploadFile(ticket, file);
    return { resourceId: ticket.resourceId, duplicate: false };
  } catch (error) {
    if (error instanceof ApiClientError && error.code === "PHOTO_DUPLICATE" && error.details && typeof error.details === "object" && "resourceId" in error.details && typeof error.details.resourceId === "string") {
      return { resourceId: error.details.resourceId, duplicate: true };
    }
    throw error;
  }
}

export function moveVehiclePhoto<T extends { id: string }>(photos: readonly T[], photoId: string, destination: number): T[] {
  const index = photos.findIndex(photo => photo.id === photoId);
  if (index < 0 || !Number.isInteger(destination) || destination < 0 || destination >= photos.length || index === destination) return [...photos];
  const next = [...photos];
  const [photo] = next.splice(index, 1);
  next.splice(destination, 0, photo!);
  return next;
}

/** Una aprobación sigue siendo descargable aunque haya otra edición pendiente. */
export function approvedVehiclePhotoCount(photos: readonly { id: string }[], versions: readonly { photo_id: string; status: string }[]): number {
  const originals = new Set(photos.map(photo => photo.id));
  return new Set(versions.filter(version => version.status === "approved" && originals.has(version.photo_id)).map(version => version.photo_id)).size;
}
